import React, { useState } from 'react';

interface ContentQualityFeedbackProps {
  html: string;
  onRegenerate: () => void;
  onModifyPrompt: () => void;
}

export const ContentQualityFeedback: React.FC<ContentQualityFeedbackProps> = ({
  html,
  onRegenerate,
  onModifyPrompt,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  // 分析HTML内容质量
  const analyzeQuality = (html: string) => {
    const issues: string[] = [];
    const suggestions: string[] = [];

    // 检查是否包含3D库
    const hasThreeJs = html.includes('three.js') || html.includes('THREE') || html.includes('three.min.js');
    const hasWebGL = html.includes('webgl') || html.includes('WebGL');
    const hasCanvas3D = html.includes('getContext(\'webgl\')') || html.includes('getContext("webgl")');

    // 检查动画复杂度
    const hasAnimation = html.includes('requestAnimationFrame') || html.includes('setInterval') || html.includes('@keyframes');
    const hasComplexAnimation = html.includes('transform') && html.includes('transition') || html.includes('animation');

    // 检查内容完整性
    const hasInteractiveElements = html.includes('onclick') || html.includes('addEventListener');

    // 检查是否是简单几何图形
    const isSimpleGeometry = (
      (html.match(/fillRect|strokeRect/g) || []).length > 0 &&
      !html.includes('image') &&
      !html.includes('img') &&
      !hasThreeJs
    );

    // 生成质量报告
    if (!hasThreeJs && !hasWebGL && !hasCanvas3D) {
      issues.push('未使用3D渲染库');
      suggestions.push('建议使用Three.js生成真正的3D效果');
    }

    if (isSimpleGeometry) {
      issues.push('检测到简单2D几何图形');
      suggestions.push('当前动画只是简单的方块移动，建议增加3D模型和真实结构');
    }

    if (!hasComplexAnimation) {
      issues.push('动画效果较简单');
      suggestions.push('可以添加更复杂的动画序列，如旋转、缩放、渐变等');
    }

    if (!hasInteractiveElements) {
      suggestions.push('可以添加交互控制，如播放/暂停按钮、视角切换等');
    }

    return {
      score: Math.max(0, 100 - issues.length * 20),
      issues,
      suggestions,
      hasThreeJs,
      hasAnimation,
      isSimpleGeometry,
    };
  };

  const quality = analyzeQuality(html);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return '良好';
    if (score >= 60) return '一般';
    return '需改进';
  };

  if (feedbackGiven) {
    return (
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-sm text-gray-600">感谢您的反馈！</p>
        <p className="text-xs text-gray-500 mt-1">我们会持续优化生成质量</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* 质量评分头部 */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">📊</span>
            <span className="text-sm font-medium text-gray-700">内容质量评估</span>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(quality.score)}`}>
            {quality.score}分 - {getScoreLabel(quality.score)}
          </div>
        </div>
      </div>

      {/* 质量详情 */}
      <div className="p-4">
        {/* 检测结果 */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-xs">
            <span className={quality.hasThreeJs ? 'text-green-500' : 'text-gray-400'}>
              {quality.hasThreeJs ? '✓' : '○'}
            </span>
            <span className={quality.hasThreeJs ? 'text-green-700' : 'text-gray-500'}>
              3D渲染支持
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className={quality.hasAnimation ? 'text-green-500' : 'text-gray-400'}>
              {quality.hasAnimation ? '✓' : '○'}
            </span>
            <span className={quality.hasAnimation ? 'text-green-700' : 'text-gray-500'}>
              动画效果
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className={!quality.isSimpleGeometry ? 'text-green-500' : 'text-yellow-500'}>
              {!quality.isSimpleGeometry ? '✓' : '!'}
            </span>
            <span className={!quality.isSimpleGeometry ? 'text-green-700' : 'text-yellow-600'}>
              内容复杂度
            </span>
          </div>
        </div>

        {/* 问题和建议 */}
        {(quality.issues.length > 0 || quality.suggestions.length > 0) && (
          <div className="mb-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
            >
              <span>{showDetails ? '隐藏' : '查看'}优化建议</span>
              <svg
                className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDetails && (
              <div className="mt-2 space-y-2">
                {quality.issues.length > 0 && (
                  <div className="bg-red-50 rounded p-2">
                    <p className="text-xs font-medium text-red-700 mb-1">检测到的问题：</p>
                    <ul className="text-xs text-red-600 space-y-0.5">
                      {quality.issues.map((issue, index) => (
                        <li key={index}>• {issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {quality.suggestions.length > 0 && (
                  <div className="bg-blue-50 rounded p-2">
                    <p className="text-xs font-medium text-blue-700 mb-1">优化建议：</p>
                    <ul className="text-xs text-blue-600 space-y-0.5">
                      {quality.suggestions.map((suggestion, index) => (
                        <li key={index}>• {suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-2">
          <button
            onClick={() => {
              onRegenerate();
              setFeedbackGiven(true);
            }}
            className="flex-1 px-3 py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors"
          >
            🔄 重新生成
          </button>
          <button
            onClick={() => {
              onModifyPrompt();
              setFeedbackGiven(true);
            }}
            className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition-colors"
          >
            ✏️ 修改提示词
          </button>
        </div>

        {/* 提示 */}
        <p className="text-xs text-gray-400 mt-3 text-center">
          不满意？尝试使用"Three.js"、"3D模型"、"WebGL"等关键词
        </p>
      </div>
    </div>
  );
};

export default ContentQualityFeedback;
